import { useCallback, useEffect, useState } from 'react';

interface ServiceWorkerUpdate {
  isUpdateAvailable: boolean;
  updateApp: () => void;
}

export function useServiceWorker(): ServiceWorkerUpdate {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [broadcastChannel, setBroadcastChannel] =
    useState<BroadcastChannel | null>(null);

  const checkAndRegisterServiceWorker = useCallback(async () => {
    try {
      // Get current SW version from localStorage
      const storedVersion = localStorage.getItem('sw_version');

      // Fetch the current SW file to check its version
      const swResponse = await fetch('/sw.js', { cache: 'no-cache' });
      const swContent = await swResponse.text();

      // Extract version from SW content
      const versionMatch = swContent.match(
        /CACHE_VERSION\s*=\s*['"]([^'"]+)['"]/
      );
      const currentVersion = versionMatch ? versionMatch[1] : 'unknown';

      // console.log('SW version check:', {
      //   stored: storedVersion,
      //   current: currentVersion,
      // });

      // Check if we need to unregister old SW
      if (storedVersion && storedVersion !== currentVersion) {
        console.log('SW version changed, unregistering old SW');
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      // Store the current version
      localStorage.setItem('sw_version', currentVersion);

      // Check if SW is already registered
      const existingReg = await navigator.serviceWorker.getRegistration(
        '/sw.js'
      );

      if (existingReg && storedVersion === currentVersion) {
        // Use existing registration (no version change)
        setRegistration(existingReg);
        setupUpdateListeners(existingReg);
      } else {
        // Register new service worker (first time or after version change)
        const reg = await navigator.serviceWorker.register('/sw.js', {
          updateViaCache: 'none',
        });
        setRegistration(reg);
        setupUpdateListeners(reg);
      }
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      checkAndRegisterServiceWorker();
    }

    // Initialize broadcast channel for multi-tab communication
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('sw-updates');
      setBroadcastChannel(channel);

      // Listen for update messages from other tabs
      channel.onmessage = (event) => {
        if (event.data.type === 'RELOAD_ALL') {
          console.log('Reloading tab due to update in another tab');
          window.location.reload();
        }
      };

      // Cleanup function
      return () => {
        channel.close();
      };
    }
  }, [checkAndRegisterServiceWorker]);

  const setupUpdateListeners = (reg: ServiceWorkerRegistration) => {
    console.log('Service Worker registered successfully');

    // Check for updates immediately
    reg.update();

    // Check for updates every minute
    setInterval(() => {
      reg.update();
    }, 60000);

    // Check for updates
    reg.addEventListener('updatefound', () => {
      const newWorker = reg.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (
            newWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            setIsUpdateAvailable(true);
          }
        });
      }
    });

    // Check if there's already a waiting worker
    if (reg.waiting && navigator.serviceWorker.controller) {
      setIsUpdateAvailable(true);
    }

    // Handle controller change (update is ready)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      setIsUpdateAvailable(false);
    });
  };

  const updateApp = () => {
    if (registration && registration.waiting) {
      // Broadcast to all other tabs to reload
      if (broadcastChannel) {
        broadcastChannel.postMessage({ type: 'RELOAD_ALL' });
      }

      // Send message to service worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });

      // Reload this tab
      window.location.reload();
    }
  };

  return {
    isUpdateAvailable,
    updateApp,
  };
}
